import {isNotNil} from 'ramda';

import {buildObject} from 'shared/web';

import {omneticApi} from '../../baseApi/omneticApi';
import {
  GetBillingInformationV2ApiArg,
  GetBillingInformationV2ApiRes,
  GetBranchV2ApiRes,
  GetTenantV2ApiRes,
  GetBranchV2ApiArg,
  PostBillingInformationV2ApiArg,
  PutBillingInformationV2ApiArg,
  PutTenantV2ApiReq,
  PutBranchV2ApiRes,
  PutBranchV2ApiArg,
  PostBranchV2ApiRes,
  PostBranchV2ApiArg,
  BulkUpdateOpeningHoursRequestBody,
  GetEmployeeByUserIdApiRes,
  GetEmployeeByUserIdApiArg,
  ActivateMultiFactorAuthenticationApiArg,
  LoginUserMultiFactorAuthenticationApiRes,
  MultiFactorAuthenticationSettingsApiRes,
  UserMultiFactorAuthenticationApiRes,
  MultiFactorAuthenticationTotpGenerateApiArg,
  MultiFactorAuthenticationTotpGenerateApiRes,
  MultiFactorAuthenticationTotpVerifyApiArg,
  MultiFactorAuthenticationTotpVerifyApiRes,
  MultiFactorAuthenticationTotpDeactivateApiArg,
  MultiFactorAuthenticationTotpDeactivateApiRes,
  MultiFactorAuthenticationTotpResetApiRes,
  MultiFactorAuthenticationTotpResetApiArg,
  PostOAuthTokenApiArg,
  PostOAuthTokenApiRes,
} from './types';

type Headers = {
  'Content-Type': string;
  'X-Workspace': string;
};

export const tenantNewApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getTenantV2: build.query<GetTenantV2ApiRes, void>({
      query: () => ({
        url: `/dms/v2/tenant`,
      }),
      providesTags: ['Tenant'],
    }),
    putTenantV2: build.mutation<void, PutTenantV2ApiReq>({
      query: (body) => ({
        url: `/dms/v2/tenant`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Tenant'],
    }),
    postBillingInformation: build.mutation<
      GetBillingInformationV2ApiRes,
      PostBillingInformationV2ApiArg
    >({
      query: (body) => ({
        url: `/dms/v3/tenant/billing-information`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [
        {type: 'BillingInformation', id: result?.id},
        'BillingInformationList',
        'Branch',
      ],
    }),
    putBillingInformation: build.mutation<
      GetBillingInformationV2ApiRes,
      PutBillingInformationV2ApiArg
    >({
      query: ({id, ...body}) => ({
        url: `/dms/v3/tenant/billing-information/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'BillingInformation', id: queryArg.id},
        'BillingInformationList',
        'Branch',
      ],
    }),
    getBillingInformationV2: build.query<
      GetBillingInformationV2ApiRes,
      GetBillingInformationV2ApiArg
    >({
      query: ({id}) => ({
        url: `/dms/v2/tenant/billing-information/${id}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'BillingInformation', id: queryArg.id},
        'BillingInformationList',
      ],
    }),
    getBranchV2: build.query<GetBranchV2ApiRes, GetBranchV2ApiArg>({
      query: ({id}) => ({
        url: `/dms/v2/tenant/branch/${id}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'Branch', id: queryArg.id}],
    }),
    putBranchV2: build.mutation<PutBranchV2ApiRes, PutBranchV2ApiArg>({
      query: ({id, ...body}) => ({
        url: `/dms/v2/tenant/branch/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'Branch', id: queryArg.id},
        'BranchList',
      ],
    }),
    postBranchV2: build.mutation<PostBranchV2ApiRes, PostBranchV2ApiArg>({
      query: (body) => ({
        url: `/dms/v2/tenant/branch`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [{type: 'Branch', id: result?.id}, 'BranchList'],
    }),
    bulkUpdateOpeningHours: build.mutation<void, BulkUpdateOpeningHoursRequestBody>({
      query: (body) => ({
        url: '/dms/v2/tenant/branch/opening-hours/bulk',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'BranchList',
        ...arg.branchIds.map((id) => ({type: 'Branch' as const, id})),
      ],
    }),
    getEmployeeByUserId: build.query<GetEmployeeByUserIdApiRes, GetEmployeeByUserIdApiArg>({
      query: ({userId}) => ({
        url: `/dms/v1/employee/by-user/${userId}`,
      }),
    }),
    activateMultiFactorAuthentication: build.mutation<
      void,
      ActivateMultiFactorAuthenticationApiArg
    >({
      query: (body) => ({
        url: '/dms/v1/auth/multi-factor-authentication/activate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MultiFactorAuthentication'],
    }),
    deactivateMultiFactorAuthentication: build.mutation<void, void>({
      query: () => ({
        url: '/dms/v1/auth/multi-factor-authentication/deactivate',
        method: 'POST',
      }),
      invalidatesTags: ['MultiFactorAuthentication'],
    }),
    getLoginUserMultiFactorAuthentication: build.query<
      LoginUserMultiFactorAuthenticationApiRes,
      void
    >({
      query: () => ({
        url: '/dms/v1/auth/login/user/multi-factor-authentication',
      }),
      providesTags: ['MultiFactorAuthentication'],
    }),
    getMultiFactorAuthenticationSettings: build.query<
      MultiFactorAuthenticationSettingsApiRes,
      void
    >({
      query: () => ({
        url: '/dms/v1/auth/multi-factor-authentication/settings',
      }),
      providesTags: ['MultiFactorAuthentication'],
    }),
    getMyMultiFactorAuthenticationSettings: build.query<UserMultiFactorAuthenticationApiRes, void>({
      query: () => ({
        url: '/dms/v1/user/me/multi-factor-authentication',
      }),
      providesTags: ['MultiFactorAuthentication'],
    }),
    getMultiFactorAuthenticationTotpGenerate: build.query<
      MultiFactorAuthenticationTotpGenerateApiRes,
      MultiFactorAuthenticationTotpGenerateApiArg
    >({
      query: () => ({
        url: '/dms/v1/auth/multi-factor-authentication/totp/generate',
        method: 'POST',
      }),
      providesTags: ['MultiFactorAuthentication'],
    }),
    verifyMultiFactorAuthenticationTotp: build.mutation<
      MultiFactorAuthenticationTotpVerifyApiRes,
      MultiFactorAuthenticationTotpVerifyApiArg
    >({
      query: (body) => ({
        url: '/dms/v1/auth/multi-factor-authentication/totp/verify',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MultiFactorAuthentication'],
    }),
    deactivateUserMultiFactorAuthenticationMutation: build.mutation<
      MultiFactorAuthenticationTotpDeactivateApiRes,
      MultiFactorAuthenticationTotpDeactivateApiArg
    >({
      query: ({userId}) => ({
        url: `/dms/v1/user/${userId}/multi-factor-authentication/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['MultiFactorAuthentication'],
    }),
    resetUserMultiFactorAuthenticationMutation: build.mutation<
      MultiFactorAuthenticationTotpResetApiRes,
      MultiFactorAuthenticationTotpResetApiArg
    >({
      query: ({userId}) => ({
        url: `/dms/v1/user/${userId}/multi-factor-authentication/reset`,
        method: 'POST',
      }),
      invalidatesTags: ['MultiFactorAuthentication'],
    }),
    postOAuthToken: build.mutation<PostOAuthTokenApiRes, PostOAuthTokenApiArg>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('grant_type', body.grant_type);
        formData.append('code', body.code);
        formData.append('redirect_uri', body.redirect_uri);
        formData.append('client_id', body.client_id);

        return {
          url: '/dms/v1/auth/oauth/token',
          method: 'POST',
          body: formData.toString(),
          headers: buildObject<Headers>()
            ['Content-Type']('application/x-www-form-urlencoded')
            ['X-Workspace'](body.workspace ?? '', isNotNil(body.workspace))
            .build(),
        };
      },
    }),
  }),
});

export const {
  useDeactivateUserMultiFactorAuthenticationMutationMutation,
  useResetUserMultiFactorAuthenticationMutationMutation,
  useGetMyMultiFactorAuthenticationSettingsQuery,
  useGetMultiFactorAuthenticationTotpGenerateQuery,
  useVerifyMultiFactorAuthenticationTotpMutation,
  useGetEmployeeByUserIdQuery,
  usePostBranchV2Mutation,
  useGetBranchV2Query,
  usePutBranchV2Mutation,
  useBulkUpdateOpeningHoursMutation,
  useGetTenantV2Query,
  usePutTenantV2Mutation,
  usePostBillingInformationMutation,
  usePutBillingInformationMutation,
  useGetBillingInformationV2Query,
  useActivateMultiFactorAuthenticationMutation,
  useDeactivateMultiFactorAuthenticationMutation,
  useGetLoginUserMultiFactorAuthenticationQuery,
  useGetMultiFactorAuthenticationSettingsQuery,
  usePostOAuthTokenMutation,
} = tenantNewApi;
